import type { SiteSections } from "@ecliptica/config/site-sections";
import { GAME_RELEASE, gameFileUrl, type GameFile } from "@ecliptica/config/game-release";
import { intlLocale, useTranslation, type Lang } from "@ecliptica/i18n";
import { Button } from "@ecliptica/ui";
import { DOWNLOADS, type DownloadKey } from "../../content/site";
import { downloadPanels } from "../../lib/sections";
import { Requirements } from "./Requirements";
import { Panel, Section } from "./Section";

// Colunas da grelha conforme o número de painéis visíveis (classes escritas por inteiro para o Tailwind as ver).
const GRID = { 1: "max-w-[540px]", 2: "max-w-[1100px] md:grid-cols-2", 3: "max-w-[1100px] md:grid-cols-3" } as const;

export function DownloadsSection({ lang, sections: on }: { lang: Lang; sections: SiteSections }) {
  const { t } = useTranslation();
  const panels = downloadPanels(on);
  if (!panels.length) return null;
  const file = (k: DownloadKey) => (
    <Button asChild size="sm" className="my-1">
      <a href={DOWNLOADS[lang][k]} download>
        {t(`dl.files.${k}`)}
      </a>
    </Button>
  );
  // Jogo: os ficheiros da versão atual (packages/config/src/game-release.json); sem ficheiros → "em breve".
  const mb = (n: number) => new Intl.NumberFormat(intlLocale(lang), { maximumFractionDigits: 0 }).format(n / 1024 / 1024);
  const releaseDate = new Intl.DateTimeFormat(intlLocale(lang), { dateStyle: "long", timeZone: "UTC" }).format(new Date(GAME_RELEASE.date));
  const game = (f: GameFile) => (
    <div key={f.platform} className="mt-2 w-full">
      <Button asChild size="sm" className="my-1">
        <a href={gameFileUrl(f.file)} download>
          {t(`dl.platform.${f.platform}`)} · {t("dl.size", { mb: mb(f.size) })}
        </a>
      </Button>
      <p className="text-sm text-muted-foreground">{t(`dl.install.${f.platform}`)}</p>
      {f.platform === "macos" && <p className="mt-1 text-sm text-muted-foreground">{t("dl.macosDamaged")}</p>}
      <details className="text-xs text-muted-foreground">
        <summary className="cursor-pointer">SHA-256</summary>
        <code className="break-all">{f.sha256}</code>
      </details>
    </div>
  );
  return (
    <Section id="download" title={t("dl.title")} bg="/bg/packs.webp">
      <div className={`mx-auto grid gap-4.5 ${GRID[panels.length as 1 | 2 | 3]}`}>
        {panels.includes("game") && (
          <Panel className="flex flex-col items-start gap-1 p-5.5">
            <h3 className="mb-1 text-xl font-extrabold text-gold">{t("dl.game")}</h3>
            {GAME_RELEASE.files.length ? (
              <>
                <p className="text-sm text-muted-foreground">
                  {t("dl.version", { version: GAME_RELEASE.version })} · {t("dl.released", { date: releaseDate })}
                </p>
                {GAME_RELEASE.files.map(game)}
              </>
            ) : (
              <Button size="sm" disabled className="my-1">
                {t("dl.platform.windows")} · <em className="font-sans">{t("dl.soon")}</em>
              </Button>
            )}
          </Panel>
        )}
        {panels.includes("books") && (
          <Panel className="flex flex-col items-start gap-1 p-5.5">
            <h3 className="mb-2 text-xl font-extrabold text-gold">{t("nav.books")}</h3>
            {file("bookPdf")}
            {file("bookEpub")}
          </Panel>
        )}
        {panels.includes("comic") && (
          <Panel className="flex flex-col items-start gap-1 p-5.5">
            <h3 className="mb-2 text-xl font-extrabold text-gold">{t("dl.comic")}</h3>
            {file("comicPdf")}
          </Panel>
        )}
      </div>
      {panels.includes("game") && GAME_RELEASE.files.length > 0 && <Requirements />}
    </Section>
  );
}
